import { withTranslation } from 'react-i18next';

import Metric from '../../../../components/Metric';
import { METRIC_COLORS, METRIC_FORMATS, deltaLabel, formatMetric } from '../format';
import { LOWER_IS_BETTER, metricDelta } from '../metrics';

/**
 * A row of headline metrics, drawn as the row the shared chart component puts above its own
 * chart: the `.metrics` strip of `metricType="primary"` cards, at the same size and spacing, so a
 * block reads as one of this app's charts rather than as a panel of its own.
 *
 * The numbers are whatever the caller resolved them to — a property aggregate, the sum of a day
 * series — and the baseline column is only turned into a delta when `comparison.comparable` says
 * the two periods may be compared at all. Selecting a card tells the caller which series its
 * chart should draw; nothing is fetched.
 *
 * Which metrics there are is the search type's business: Discover and Google News report no
 * ranking position, so that card is absent there rather than empty.
 */
function MetricCards({
    t,
    metrics,
    totals,
    priorTotals,
    comparison,
    selectedMetric,
    onSelectMetric,
    labelFor,
    noteFor,
}) {
    return (
        <div className="metrics mb-2">
            {metrics.map((name) => {
                const value = totals ? totals[name] : null;
                const prior = priorTotals ? priorTotals[name] : null;
                const delta = comparison?.comparable ? metricDelta(name, value, prior) : null;
                const label = delta ? deltaLabel(t, name, delta) : null;

                const improved = LOWER_IS_BETTER[name] ? delta?.absolute < 0 : delta?.absolute > 0;
                const worsened = LOWER_IS_BETTER[name] ? delta?.absolute > 0 : delta?.absolute < 0;

                const defaultNote = !totals
                    ? t('tools.gsc.metrics.unavailable')
                    : totals.reason
                    ? t(`tools.gsc.reasons.${totals.reason}`)
                    : comparison?.comparable
                    ? t('tools.gsc.metrics.versus_prior', { value: formatMetric(name, prior) })
                    : t(`tools.gsc.comparison_unavailable.${comparison?.reason ?? 'no_baseline'}`);

                return (
                    <Metric
                        key={name}
                        metricType="primary"
                        color={METRIC_COLORS[name]}
                        label={labelFor ? labelFor(name) : t(`tools.gsc.metrics.${name}`)}
                        value={value}
                        format={METRIC_FORMATS[name]}
                        changeLabel={
                            label && LOWER_IS_BETTER[name] && improved
                                ? `${label} · ${t('tools.gsc.metrics.improved')}`
                                : label
                        }
                        changeDirection={improved ? 'up' : worsened ? 'down' : null}
                        note={noteFor ? noteFor(name, defaultNote) : defaultNote}
                        selected={selectedMetric === name}
                        onClick={onSelectMetric ? () => onSelectMetric(name) : undefined}
                    />
                );
            })}
        </div>
    );
}

export default withTranslation()(MetricCards);
